// src/components/ArticleReader/ArticleReader.tsx
import React, { useRef } from 'react';
import { Article, CategorieArticle, SousSection } from '../../data/types';
import Footer from '../Footer/Footer';
import './ArticleReader.css';

interface ArticleReaderProps {
  article: Article;
  categorie: CategorieArticle;
  onBack: () => void;
}

const ArticleReader: React.FC<ArticleReaderProps> = ({ article, categorie, onBack }) => {
  const articleRef = useRef<HTMLDivElement>(null);

  // Fonction pour convertir le markdown basique en HTML
  const parseMarkdown = (text: string): string => {
    if (!text) return '';
    
    return text
      // Gras : **texte** → <strong>texte</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italique : *texte* → <em>texte</em>
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Retours à la ligne doubles → paragraphes
      .replace(/\n\n/g, '</p><p>')
      // Retours à la ligne simples → <br>
      .replace(/\n/g, '<br>');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderSousSection = (sousSection: SousSection) => {
    return (
      <div key={sousSection.id} className="sous-section">
        <h4 className="sous-section-titre">{sousSection.titre}</h4>
        
        {sousSection.contenu && (
          <div 
            className="sous-section-contenu"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.contenu) }}
          />
        )}

        {sousSection.elements && (
          <div className="elements-container">
            {sousSection.elements.map((element, index) => (
              <div key={index} className={`element element-${element.type}`}>
                {element.type === 'liste' && element.items ? (
                  <ul className="element-liste">
                    {element.items.map((item, itemIndex) => (
                      <li key={itemIndex} dangerouslySetInnerHTML={{ __html: parseMarkdown(item) }} />
                    ))}
                  </ul>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: parseMarkdown(element.texte || '') }} />
                )}
              </div>
            ))}
          </div>
        )}

        {sousSection.formule && (
          <div className="formule-container">
            {sousSection.formule.description && (
              <div 
                className="formule-description"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.formule.description) }}
              />
            )}
            <div className="formule-arabe">{sousSection.formule.arabe}</div>
            {sousSection.formule.phonetique && (
              <div className="formule-phonetique">{sousSection.formule.phonetique}</div>
            )}
            {sousSection.formule.traduction && (
              <div className="formule-traduction">{sousSection.formule.traduction}</div>
            )}
          </div>
        )}

        {sousSection.invocation && (
          <div className="invocation-container">
            <div className="invocation-arabe">{sousSection.invocation.arabe}</div>
            <div className="invocation-phonetique">{sousSection.invocation.phonetique}</div>
            <div className="invocation-traduction">{sousSection.invocation.traduction}</div>
            <div className="invocation-reference">{sousSection.invocation.reference}</div>
          </div>
        )}

        {sousSection.hadith && (
          <div className="hadith-context">
            <div 
              className="hadith-rapporteur"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.hadith.rapporteur) }}
            />
            <div className="hadith-texte">« {sousSection.hadith.texte} »</div>
            {sousSection.hadith.dialogue && (
              <div 
                className="hadith-dialogue"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.hadith.dialogue) }}
              />
            )}
            {sousSection.hadith.reponse && (
              <div className="hadith-reponse">« {sousSection.hadith.reponse} »</div>
            )}
            <div className="hadith-reference">{sousSection.hadith.reference}</div>
          </div>
        )}

        {sousSection.noteImportante && (
          <div className="note-importante">
            <div 
              className="note-contenu"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.noteImportante) }}
            />
          </div>
        )}

        {sousSection.justification && (
          <div 
            className="justification"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.justification) }}
          />
        )}

        {sousSection.nuance && (
          <div 
            className="nuance"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(sousSection.nuance) }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="article-reader">
      {/* Navigation de retour simple */}
      <div className="article-nav">
        <button onClick={onBack} className="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path 
              d="M19 12H5M12 19l-7-7 7-7" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          Retour aux articles
        </button>
      </div>

      {/* Contenu de l'article */}
      <div ref={articleRef} className="article-content">
        {/* En-tête de l'article */}
        <div className="article-header">
          <div className="article-category-badge" style={{ backgroundColor: categorie.couleur }}>
            {categorie.titre}
          </div>
          
          <h1 className="article-main-title">{article.titre}</h1>
          
          {article.sousTitre && (
            <h2 className="article-main-subtitle">{article.sousTitre}</h2>
          )}
          
          <div className="article-meta-info">
            <div className="article-meta-item">
              <span className="meta-label">Publié le</span>
              <span className="meta-value">{formatDate(article.datePublication)}</span>
            </div>
            <div className="article-meta-item">
              <span className="meta-label">Lecture</span>
              <span className="meta-value">{article.tempsLecture}</span>
            </div>
            <div className="article-meta-item">
              <span className="meta-label">Par</span>
              <span className="meta-value">{article.auteur}</span>
            </div>
          </div>

          <div className="article-tags-container">
            {article.tags.map((tag, index) => (
              <span key={index} className="article-tag">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Introduction */}
        {article.content?.introduction && (
          <div className="article-introduction">
            {article.content.introduction.versetOuverture && (
              <div className="verset-ouverture">
                <div className="verset-arabe">
                  {article.content.introduction.versetOuverture.arabe}
                </div>
                <div className="verset-traduction">
                  {article.content.introduction.versetOuverture.traduction}
                </div>
                <div className="verset-reference">
                  {article.content.introduction.versetOuverture.reference}
                </div>
              </div>
            )}

            {article.content.introduction.versetPrincipal && (
              <div className="verset-principal">
                <div className="verset-intro">Allah dit :</div>
                <div className="verset-arabe">
                  {article.content.introduction.versetPrincipal.arabe}
                </div>
                <div className="verset-traduction">
                  {article.content.introduction.versetPrincipal.traduction}
                </div>
                <div className="verset-reference">
                  {article.content.introduction.versetPrincipal.reference}
                </div>
              </div>
            )}

            {article.content.introduction.texte && (
              <div 
                className="introduction-texte"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(article.content.introduction.texte) }}
              />
            )}
          </div>
        )}

        {/* Sections principales */}
        <div className="article-sections">
          {article.content?.sections.map((section) => (
            <div key={section.id} className={`article-section section-${section.type}`}>
              <h3 className="section-titre">{section.titre}</h3>
              
              {section.contenu && (
                <div 
                  className="section-contenu"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(section.contenu) }}
                />
              )}

              <div className="sous-sections-container">
                {section.sousSecions.map(renderSousSection)}
              </div>
            </div>
          ))}
        </div>

        {/* Conclusion */}
        {article.content?.conclusion && (
          <div className="article-conclusion">
            {article.content.conclusion.texte && (
              <div 
                className="conclusion-texte"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(article.content.conclusion.texte) }}
              />
            )}
            <div className="conclusion-signature">
              {article.content.conclusion.signature}
            </div>
          </div>
        )}
      </div>

      {/* Footer identique à la page d'accueil */}
      <Footer texts={[]} />
    </div>
  );
};

export default ArticleReader;
